import type { WeeklyGameId } from '../../lib/api/userFlags'
import { useT } from '../../i18n'

/**
 * First-visit explainer for the weekly games. Shown once per ACCOUNT
 * (flag lives server-side in `user_flags`), so a player who saw it on
 * web doesn't see it again on their phone. Profile → "Show game tips
 * again" clears the flags and re-arms all three.
 */

interface TipConfig {
  title: string
  art: string
  cardClass: string
  accent: string
  btnClass: string
  steps: string[]
  cta: string
}

const TIP: Record<WeeklyGameId, TipConfig> = {
  colorful: {
    title: 'WEEKLY COLORFUL',
    art: '/games/colorful.webp',
    cardClass: 'bg-game-colorful-light border-game-colorful-dark',
    accent: '#74705F',
    btnClass: 'btn-colorful',
    steps: [
      'Every week, six new subjects are chosen.',
      'The entire community picks the color they think best represents each subject. Scroll through and lock in your six.',
      'At the start of next week the results are revealed. Players matching the crowd’s majority color on the most subjects earn the weekly award.',
    ],
    cta: 'Start picking colors',
  },
  three_step: {
    title: 'WEEKLY THREE STEP',
    art: '/games/three-step.webp',
    cardClass: 'bg-game-three-step-light border-game-three-step-dark',
    accent: '#0EAE93',
    btnClass: 'btn-three-step',
    steps: [
      'Every week there’s a new prompt: two everyday actions you have to pull off at the same time.',
      'Write your best three-step solution before the week ends.',
      'Next week the community votes on every solution. The top vote-getter earns the weekly award.',
    ],
    cta: 'Got it',
  },
  ideatthat: {
    title: 'Weekly I’d Eat That',
    art: '/games/ideatthat.webp',
    cardClass: 'iet-gold border-game-iet-dark',
    accent: '#B58615',
    btnClass: 'btn-iet',
    steps: [
      'Every Monday a fresh set of ingredients drops: three shared ones everyone gets, plus one unique one just for you.',
      'Write a creative food adventure out of them before the week ends.',
      'Next week the community reads and votes. The story with the most votes takes the weekly trophy.',
    ],
    cta: 'Got it',
  },
}

export function WeeklyTipOverlay({
  game, onDismiss,
}: {
  game: WeeklyGameId
  onDismiss: () => void
}) {
  const t = useT()
  const tip = TIP[game]
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6"
         role="dialog" aria-modal="true" aria-label={t('How {game} works', { game: t(tip.title) })}>
      <div className={`w-full max-w-sm rounded-3xl border-2 shadow-card p-6 ${tip.cardClass}`}>
        <div className="flex items-center gap-3 mb-4">
          <img src={tip.art} alt="" className="w-14 h-14 rounded-2xl object-cover shadow-xs" />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest"
               style={{ color: tip.accent }}>{t('How it works')}</p>
            <h2 className="font-display text-xl font-bold text-brand-text">{t(tip.title)}</h2>
          </div>
        </div>

        <ol className="space-y-3 mb-5">
          {tip.steps.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center
                               font-display font-bold text-sm text-white"
                    style={{ backgroundColor: tip.accent }}>
                {i + 1}
              </span>
              <p className="text-sm text-brand-text leading-snug">{t(s)}</p>
            </li>
          ))}
        </ol>

        <button onClick={onDismiss} className={tip.btnClass}>
          {t(tip.cta)}
        </button>
      </div>
    </div>
  )
}

